/**
 * Azioni amministrative su un registro (CF10) — CL05.
 *
 * Reset e override toccano una numerazione **in uso**: il primo riporta il
 * cursore all'inizio, il secondo lo sposta. Entrambi possono far ripetere un
 * codice già emesso, ed è esattamente il motivo per cui il contratto chiede una
 * parola di conferma e un motivo. La parola non la chiede all'utente: il client
 * manda `RESET_KEYWORD`, che è l'unica grafia accettata. Il motivo invece lo
 * scrive l'utente, e il server lo pretende (`_require_reason`): senza, l'azione
 * torna 422.
 *
 * Due cose che questo file non fa, deliberatamente:
 *
 * - **Non calcola i codici.** L'anteprima è del server e il contratto la marca
 *   `advisory`: mostrarne una calcolata qui significherebbe promettere un numero
 *   che un altro avvio può prendere nel frattempo.
 * - **Non aggiorna l'audit in modo ottimistico.** Dopo il successo lo rilegge:
 *   una voce aggiunta dal client può descrivere un'operazione che il server ha
 *   registrato diversamente.
 */

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import React, { useEffect, useMemo, useState } from "react";
import DialogHeader from "components/dialogs/DialogHeader";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import type { AuditEvent, RegistryPreview, RegistryRead } from "model/CustomFields";
import { customFieldsApi } from "api/customFieldsApi";
import { newFieldId } from "./fieldTypes";
import { canSubmitReset, nextAfter, previewIsAdvisory, RESET_KEYWORD } from "./sequences";

type Mode = "reset" | "override";

interface SequenceActionsDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  registry: RegistryRead | null;
  /** Chiamata dopo un'azione riuscita, con il registro aggiornato. */
  onDone?: (registry: RegistryRead) => void;
}

const SequenceActionsDialog: React.FC<SequenceActionsDialogProps> = ({
  open,
  onClose,
  organizationId,
  registry,
  onDone,
}) => {
  const translations = useTranslations();
  const [mode, setMode] = useState<Mode>("reset");
  const [reason, setReason] = useState("");
  const [lastIssued, setLastIssued] = useState("");
  const [preview, setPreview] = useState<RegistryPreview | null>(null);
  const [audit, setAudit] = useState<AuditEvent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ogni apertura riparte pulita: il motivo vale per l'operazione che si sta
  // guardando, non per quella precedente.
  useEffect(() => {
    if (!open) return;
    setReason("");
    setLastIssued(registry?.lastIssued ?? "");
    setAudit(null);
    setError(null);
  }, [open, registry?.registryId, registry?.lastIssued]);

  // L'anteprima si carica all'apertura: e' indicativa e non riserva nulla, ma
  // mostra cosa comporterebbe l'azione.
  useEffect(() => {
    if (!open || !registry) return;
    let cancelled = false;
    setPreview(null);
    customFieldsApi
      .previewRegistry(organizationId, registry.registryId, {
        operationId: newFieldId(),
        expectedRevision: registry.revision,
      })
      .then((anteprima) => {
        if (!cancelled) setPreview(anteprima);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [open, organizationId, registry?.registryId, registry?.revision]);

  // `canSubmitReset` risponde del motivo scritto: la parola la fornisce il
  // client, quindi non e' una cosa da verificare.
  const reset = useMemo(() => canSubmitReset({ reason }), [reason]);

  const canSubmit =
    registry !== null &&
    (mode === "reset"
      ? reset.ok
      : reason.trim() !== "" &&
        lastIssued.trim() !== "" &&
        Number.isInteger(Number(lastIssued)) &&
        Number(lastIssued) >= 0);

  const submit = async () => {
    if (!registry) return;
    setLoading(true);
    setError(null);
    try {
      const operationId = newFieldId();
      const base = {
        operationId,
        expectedRevision: registry.revision,
        reason,
      };

      const aggiornato =
        mode === "reset"
          ? await customFieldsApi.resetRegistry(organizationId, registry.registryId, {
              ...base,
              // Il server pretende la parola: la fornisce il client, che e'
              // l'unica grafia accettata.
              keyword: RESET_KEYWORD,
            })
          : await customFieldsApi.overrideRegistry(organizationId, registry.registryId, {
              ...base,
              lastIssued: Number(lastIssued),
            });

      // L'audit si **rilegge**: una voce composta dal client descriverebbe
      // l'operazione come il client la immagina, non come il server l'ha
      // registrata.
      const pagina = await customFieldsApi.getRegistryAudit(
        organizationId,
        registry.registryId
      );
      setAudit(pagina.items ?? []);
      onDone?.(aggiornato);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  if (!registry) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth id="sequence-actions-dialog">
      <DialogHeader sx={{ px: 4 }} onClose={onClose}>
        {getTranslation(translations, "custom_fields.sequence.actions")}
      </DialogHeader>
      <DialogContent>
        <Stack gap={2} pt={1}>
          <Typography variant="subtitle2" color="text.secondary">
            {registry.name} ({registry.code})
          </Typography>

          <TextField
            select
            size="small"
            fullWidth
            label={getTranslation(translations, "custom_fields.sequence.action")}
            SelectProps={{ "data-testid": "cf-sequence-mode" } as never}
            value={mode}
            onChange={(e) => setMode(e.target.value as Mode)}
          >
            <MenuItem value="reset">
              {getTranslation(translations, "custom_fields.sequence.reset.title")}
            </MenuItem>
            <MenuItem value="override">
              {getTranslation(translations, "custom_fields.sequence.override.title")}
            </MenuItem>
          </TextField>

          {mode === "override" && (
            <Stack gap={0.5}>
              <TextField
                size="small"
                fullWidth
                type="number"
                label={getTranslation(translations, "custom_fields.sequence.override.last_issued")}
                inputProps={{ "data-testid": "cf-override-last-issued" }}
                value={lastIssued}
                onChange={(e) => setLastIssued(e.target.value)}
              />
              {/* Il successivo si mostra: lasciare solo il campo da compilare
                  obbligherebbe a calcolare a mente cosa comporta. */}
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="cf-override-next"
              >
                {getTranslation(translations, "custom_fields.sequence.override.next")}
                {": "}
                {String(nextAfter(lastIssued === "" ? null : Number(lastIssued)) ?? "—")}
              </Typography>
            </Stack>
          )}

          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label={getTranslation(translations, "custom_fields.sequence.reason")}
            data-testid="cf-sequence-reason"
            // Vuoto di proposito: un motivo precompilato e' un motivo che
            // nessuno scrive, e il server lo conserva nell'audit.
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />

          {loading && (
            <Stack alignItems="center">
              <CircularProgress size={20} data-testid="cf-sequence-loading" />
            </Stack>
          )}

          {error && (
            <Alert severity="error" data-testid="cf-sequence-error">
              {error}
            </Alert>
          )}

          {preview && (
            <Box data-testid="cf-sequence-preview">
              <Typography variant="caption" color="text.secondary">
                {/* Indicativa per contratto: non riserva nulla. */}
                {getTranslation(translations, "custom_fields.sequence.preview.advisory")}
              </Typography>
              <Typography variant="body2">
                {preview.sampleCodes.join(" · ")}
              </Typography>
              {!previewIsAdvisory(preview) && (
                <Alert severity="warning" data-testid="cf-sequence-preview-binding">
                  {getTranslation(translations, "custom_fields.sequence.preview.not_advisory")}
                </Alert>
              )}
            </Box>
          )}

          {audit && (
            <Box data-testid="cf-sequence-audit">
              <Typography variant="subtitle2">
                {getTranslation(translations, "custom_fields.sequence.audit")}
              </Typography>
              {audit.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {getTranslation(translations, "custom_fields.sequence.audit.empty")}
                </Typography>
              ) : (
                audit.map((event) => (
                  <Typography key={event.eventId} variant="body2" color="text.secondary">
                    {getTranslation(translations, event.messageKey)}
                    {event.after ? ` → ${event.after}` : ""}
                  </Typography>
                ))
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="cf-sequence-cancel">
          {getTranslation(translations, "custom_fields.action.cancel")}
        </Button>
        <LoadingButton
          loading={loading}
          disabled={!canSubmit}
          onClick={submit}
          data-testid={mode === "reset" ? "cf-reset-confirm" : "cf-override-confirm"}
        >
          {getTranslation(translations, "custom_fields.sequence.confirm")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SequenceActionsDialog;
